import nacl from 'tweetnacl';
import { createSafeRasterObjectUrl, sniffSafeRasterMime } from './safeImagePreview';

export const ENCRYPTION_VERSION = 'v1';
export const ENCRYPTION_KEY_MESSAGE_PREFIX = 'CronPay Task Encryption Key v1';
export const ENCRYPTED_TASK_TITLE_PLACEHOLDER = '[Encrypted task]';
export const ENCRYPTED_TASK_DESCRIPTION_PLACEHOLDER =
    '[Encrypted — unlock with your wallet to view]';
export const ENCRYPTED_SUBMISSION_MESSAGE_PLACEHOLDER =
    '[Encrypted submission — unlock with your wallet to view]';

const keyPairCache = new Map();
/** Prevents MetaMask spam when many tasks decrypt in parallel before the first sign finishes. */
const keyPairInFlight = new Map();
const SESSION_KEY_PREFIX = 'cronpay_enc_kp_v1:';

export const buildEncryptionKeyMessage = (walletAddress) =>
    `${ENCRYPTION_KEY_MESSAGE_PREFIX}\nWallet: ${String(walletAddress).trim().toLowerCase()}`;

export const isEncryptedTask = (task) =>
    Boolean(task?.encryptionVersion && task?.encryptedPayload);

export const isEncryptedSubmission = (submission) =>
    Boolean(submission?.encryptionVersion && submission?.encryptedPayload);

export const taskNeedsClientDecrypt = (task) => {
    if (!task) return false;
    if (isEncryptedTask(task) && !task._decrypted && !task._decryptionError) {
        return true;
    }
    if (!Array.isArray(task.submissions)) {
        return false;
    }
    return task.submissions.some(
        (submission) =>
            isEncryptedSubmission(submission) &&
            !submission._decrypted &&
            !submission._decryptionError,
    );
};

const bytesToBase64 = (bytes) => {
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
};

const base64ToBytes = (value) => {
    const binary = atob(String(value || ''));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
};

const signatureToBytes = (signature) => {
    const hex = String(signature).startsWith('0x')
        ? signature.slice(2)
        : String(signature);
    const bytes = new Uint8Array(hex.length / 2);
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Number.parseInt(hex.substr(index * 2, 2), 16);
    }
    return bytes;
};

const deriveSeedFromSignature = async (signature, walletAddress) => {
    const ikm = signatureToBytes(signature);
    const salt = new TextEncoder().encode(String(walletAddress).trim().toLowerCase());
    const info = new TextEncoder().encode('cronpay-task-encryption-v1');
    const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, [
        'deriveBits',
    ]);
    const bits = await crypto.subtle.deriveBits(
        { name: 'HKDF', hash: 'SHA-256', salt, info },
        baseKey,
        32 * 8,
    );
    return new Uint8Array(bits);
};

const readPersistedKeyPair = (normalizedWallet) => {
    if (typeof sessionStorage === 'undefined') {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${normalizedWallet}`);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed?.secretKeyBase64 || !parsed?.publicKeyBase64) {
            return null;
        }
        const secretKey = base64ToBytes(parsed.secretKeyBase64);
        const publicKey = base64ToBytes(parsed.publicKeyBase64);
        if (secretKey.length !== 32 || publicKey.length !== 32) {
            return null;
        }
        return {
            publicKey,
            secretKey,
            publicKeyBase64: parsed.publicKeyBase64,
        };
    } catch (_error) {
        return null;
    }
};

const persistKeyPair = (normalizedWallet, material) => {
    if (typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        sessionStorage.setItem(
            `${SESSION_KEY_PREFIX}${normalizedWallet}`,
            JSON.stringify({
                publicKeyBase64: material.publicKeyBase64,
                secretKeyBase64: bytesToBase64(material.secretKey),
            }),
        );
    } catch (_error) {
        // Private browsing / quota — in-memory cache still works for this tab life.
    }
};

const clearPersistedKeyPair = (normalizedWallet) => {
    if (typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        if (normalizedWallet) {
            sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${normalizedWallet}`);
            return;
        }
        const keysToRemove = [];
        for (let index = 0; index < sessionStorage.length; index += 1) {
            const key = sessionStorage.key(index);
            if (key && key.startsWith(SESSION_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (_error) {
        // ignore
    }
};

export const clearEncryptionKeyPairCache = (walletAddress) => {
    if (walletAddress) {
        const normalized = String(walletAddress).trim().toLowerCase();
        keyPairCache.delete(normalized);
        keyPairInFlight.delete(normalized);
        clearPersistedKeyPair(normalized);
        return;
    }
    keyPairCache.clear();
    keyPairInFlight.clear();
    clearPersistedKeyPair();
};

export const deriveEncryptionKeyPair = async (signMessageAsync, walletAddress) => {
    if (!signMessageAsync) {
        throw new Error('Wallet signing is required for task encryption.');
    }

    const normalized = String(walletAddress).trim().toLowerCase();
    if (keyPairCache.has(normalized)) {
        return keyPairCache.get(normalized);
    }

    const persisted = readPersistedKeyPair(normalized);
    if (persisted) {
        keyPairCache.set(normalized, persisted);
        return persisted;
    }

    if (keyPairInFlight.has(normalized)) {
        return keyPairInFlight.get(normalized);
    }

    const derivePromise = (async () => {
        const signature = await signMessageAsync({
            message: buildEncryptionKeyMessage(normalized),
        });
        const seed = await deriveSeedFromSignature(signature, normalized);
        const keyPair = nacl.box.keyPair.fromSecretKey(seed);
        const material = {
            publicKey: keyPair.publicKey,
            secretKey: keyPair.secretKey,
            publicKeyBase64: bytesToBase64(keyPair.publicKey),
        };
        keyPairCache.set(normalized, material);
        persistKeyPair(normalized, material);
        return material;
    })();

    keyPairInFlight.set(normalized, derivePromise);
    try {
        return await derivePromise;
    } finally {
        keyPairInFlight.delete(normalized);
    }
};

const generateAesRawKey = async () => {
    const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt'],
    );
    const rawKey = await crypto.subtle.exportKey('raw', key);
    return new Uint8Array(rawKey);
};

const importAesKey = async (rawKey, usages) =>
    crypto.subtle.importKey(
        'raw',
        rawKey,
        { name: 'AES-GCM', length: 256 },
        false,
        usages,
    );

const aesGcmEncrypt = async (payload, rawKey = null) => {
    const taskKey = rawKey || (await generateAesRawKey());
    const cryptoKey = await importAesKey(taskKey, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        encoded,
    );

    return {
        iv: bytesToBase64(new Uint8Array(iv)),
        ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
        taskKey,
    };
};

const aesGcmDecrypt = async ({ iv, ciphertext }, taskKey) => {
    const cryptoKey = await importAesKey(taskKey, ['decrypt']);
    const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToBytes(iv) },
        cryptoKey,
        base64ToBytes(ciphertext),
    );
    return JSON.parse(new TextDecoder().decode(decrypted));
};

export const encryptBytesWithKey = async (bytes, rawKey) => {
    const cryptoKey = await importAesKey(rawKey, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        cryptoKey,
        bytes,
    );
    return {
        iv: bytesToBase64(new Uint8Array(iv)),
        ciphertext: new Uint8Array(ciphertext),
    };
};

export const decryptBytesWithKey = async ({ iv, ciphertextBytes }, rawKey) => {
    const cryptoKey = await importAesKey(rawKey, ['decrypt']);
    const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToBytes(iv) },
        cryptoKey,
        ciphertextBytes,
    );
    return new Uint8Array(decrypted);
};

const sealForPublicKey = (message, recipientPublicKey) => {
    const ephemeralKeyPair = nacl.box.keyPair();
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const boxed = nacl.box(
        message,
        nonce,
        recipientPublicKey,
        ephemeralKeyPair.secretKey,
    );
    const sealed = new Uint8Array(
        nacl.box.publicKeyLength + nacl.box.nonceLength + boxed.length,
    );
    sealed.set(ephemeralKeyPair.publicKey, 0);
    sealed.set(nonce, nacl.box.publicKeyLength);
    sealed.set(boxed, nacl.box.publicKeyLength + nacl.box.nonceLength);
    return sealed;
};

const openSealedForKeyPair = (sealed, keyPair) => {
    const publicKeyLength = nacl.box.publicKeyLength;
    const nonceLength = nacl.box.nonceLength;
    const ephemeralPublicKey = sealed.slice(0, publicKeyLength);
    const nonce = sealed.slice(publicKeyLength, publicKeyLength + nonceLength);
    const boxed = sealed.slice(publicKeyLength + nonceLength);
    return nacl.box.open(boxed, nonce, ephemeralPublicKey, keyPair.secretKey);
};

export const wrapTaskKeyForRecipient = (taskKey, recipientPublicKeyBase64) => {
    const recipientPublicKey = base64ToBytes(recipientPublicKeyBase64);
    if (recipientPublicKey.length !== nacl.box.publicKeyLength) {
        throw new Error('Invalid encryption public key for recipient.');
    }
    return bytesToBase64(sealForPublicKey(taskKey, recipientPublicKey));
};

export const unwrapTaskKey = (wrappedBase64, keyPair) => {
    const opened = openSealedForKeyPair(base64ToBytes(wrappedBase64), keyPair);
    if (!opened) {
        throw new Error('Unable to unwrap task key for this wallet.');
    }
    return opened;
};

export const encryptTaskPayload = async (
    { title, description },
    { companyPublicKeyBase64, employeePublicKeyBase64 },
) => {
    if (!companyPublicKeyBase64 || !employeePublicKeyBase64) {
        throw new Error('Both company and employee encryption keys are required.');
    }

    const { iv, ciphertext, taskKey } = await aesGcmEncrypt({
        title: String(title || '').trim(),
        description: String(description || ''),
    });

    return {
        encryptionVersion: ENCRYPTION_VERSION,
        encryptedPayload: { iv, ciphertext },
        keyWrapCompany: wrapTaskKeyForRecipient(taskKey, companyPublicKeyBase64),
        keyWrapEmployee: wrapTaskKeyForRecipient(taskKey, employeePublicKeyBase64),
        title: ENCRYPTED_TASK_TITLE_PLACEHOLDER,
        description: ENCRYPTED_TASK_DESCRIPTION_PLACEHOLDER,
    };
};

const resolvePartyWrapField = (walletAddress, companyWallet, employeeWallet) => {
    const normalized = String(walletAddress || '').trim().toLowerCase();
    const company = String(companyWallet || '').trim().toLowerCase();
    const employee = String(employeeWallet || '').trim().toLowerCase();
    if (normalized && normalized === company) return 'keyWrapCompany';
    if (normalized && normalized === employee) return 'keyWrapEmployee';
    return null;
};

const decryptEncryptedFileToObjectUrl = async (fileMeta, submissionKey, resolveUrl) => {
    const fileUrl = resolveUrl(fileMeta.url);
    const response = await fetch(fileUrl);
    if (!response.ok) {
        throw new Error(
            `Failed to download encrypted attachment (${response.status}).`,
        );
    }
    const ciphertextBytes = new Uint8Array(await response.arrayBuffer());
    const plainBytes = await decryptBytesWithKey(
        { iv: fileMeta.iv, ciphertextBytes },
        submissionKey,
    );
    // Ignore declared MIME — only sniffed safe rasters become app-origin blob URLs.
    return createSafeRasterObjectUrl(plainBytes, fileMeta.mimeType);
};

export const decryptSubmissionPayload = async (
    submission,
    signMessageAsync,
    walletAddress,
    { companyWallet, employeeWallet, resolveAttachmentUrlFn } = {},
) => {
    if (!isEncryptedSubmission(submission)) {
        return submission;
    }

    const wrapField = resolvePartyWrapField(
        walletAddress,
        companyWallet,
        employeeWallet,
    );
    if (!wrapField) {
        return {
            ...submission,
            _decryptionError: 'You are not authorized to decrypt this submission.',
        };
    }

    const wrapped = submission[wrapField];
    if (!wrapped) {
        return {
            ...submission,
            _decryptionError: 'Missing encrypted key wrap for your wallet.',
        };
    }

    try {
        const keyPair = await deriveEncryptionKeyPair(signMessageAsync, walletAddress);
        const submissionKey = unwrapTaskKey(wrapped, keyPair);
        const fields = await aesGcmDecrypt(submission.encryptedPayload, submissionKey);
        const links = Array.isArray(fields.links) ? fields.links : [];
        const files = Array.isArray(fields.files) ? fields.files : [];
        const resolveUrl =
            typeof resolveAttachmentUrlFn === 'function'
                ? resolveAttachmentUrlFn
                : (value) => value;

        const imageObjectUrls = [];
        for (const fileMeta of files) {
            try {
                imageObjectUrls.push(
                    await decryptEncryptedFileToObjectUrl(
                        fileMeta,
                        submissionKey,
                        resolveUrl,
                    ),
                );
            } catch (_error) {
                // Keep going; UI can omit a failed image.
            }
        }

        return {
            ...submission,
            message: fields.message || '',
            attachments: [...links, ...imageObjectUrls],
            _decrypted: true,
            _decryptedLinks: links,
            _decryptedImageUrls: imageObjectUrls,
        };
    } catch (error) {
        return {
            ...submission,
            _decryptionError: error?.message || 'Submission decryption failed.',
        };
    }
};

export const decryptTaskPayload = async (
    task,
    signMessageAsync,
    walletAddress,
    options = {},
) => {
    let nextTask = task;

    if (isEncryptedTask(task)) {
        const companyWallet = String(
            task.createdByWallet || task.company?.ownerWallet || '',
        )
            .trim()
            .toLowerCase();
        const employeeWallet = String(task.employee?.wallet || '')
            .trim()
            .toLowerCase();
        const wrapField = resolvePartyWrapField(
            walletAddress,
            companyWallet,
            employeeWallet,
        );

        if (!wrapField) {
            nextTask = {
                ...task,
                _decryptionError: 'You are not authorized to decrypt this task.',
            };
        } else if (!task[wrapField]) {
            nextTask = {
                ...task,
                _decryptionError: 'Missing encrypted key wrap for your wallet.',
            };
        } else {
            try {
                const keyPair = await deriveEncryptionKeyPair(
                    signMessageAsync,
                    walletAddress,
                );
                const taskKey = unwrapTaskKey(task[wrapField], keyPair);
                const fields = await aesGcmDecrypt(task.encryptedPayload, taskKey);
                nextTask = {
                    ...task,
                    title: fields.title,
                    description: fields.description,
                    _decrypted: true,
                };
            } catch (error) {
                nextTask = {
                    ...task,
                    _decryptionError: error?.message || 'Task decryption failed.',
                };
            }
        }
    }

    if (!Array.isArray(nextTask?.submissions) || !nextTask.submissions.length) {
        return nextTask;
    }

    const companyWallet =
        nextTask.createdByWallet || nextTask.company?.ownerWallet || '';
    const employeeWallet = nextTask.employee?.wallet || '';
    const decryptedSubmissions = [];
    for (const submission of nextTask.submissions) {
        decryptedSubmissions.push(
            await decryptSubmissionPayload(
                submission,
                signMessageAsync,
                walletAddress,
                {
                    companyWallet,
                    employeeWallet,
                    resolveAttachmentUrlFn: options.resolveAttachmentUrlFn,
                },
            ),
        );
    }

    return {
        ...nextTask,
        submissions: decryptedSubmissions,
    };
};

export const decryptTasksInList = async (
    tasks,
    signMessageAsync,
    walletAddress,
    options = {},
) => {
    if (!Array.isArray(tasks) || !tasks.length || !signMessageAsync || !walletAddress) {
        return tasks || [];
    }

    // Unlock once up front so parallel list hydrators share the same key.
    const needsAnyDecrypt = tasks.some((task) => taskNeedsClientDecrypt(task));
    if (needsAnyDecrypt) {
        await deriveEncryptionKeyPair(signMessageAsync, walletAddress);
    }

    return Promise.all(
        tasks.map((task) =>
            decryptTaskPayload(task, signMessageAsync, walletAddress, options),
        ),
    );
};

export const prepareEncryptedTaskCreatePayload = async ({
    signMessageAsync,
    companyWalletAddress,
    employeeWalletAddress,
    title,
    description,
    registerEncryptionPublicKey,
    fetchEncryptionPublicKey,
}) => {
    const companyKeyPair = await deriveEncryptionKeyPair(
        signMessageAsync,
        companyWalletAddress,
    );
    await registerEncryptionPublicKey(companyKeyPair.publicKeyBase64);

    let employeePublicKeyBase64;
    try {
        employeePublicKeyBase64 = await fetchEncryptionPublicKey(employeeWalletAddress);
    } catch (_error) {
        throw new Error(
            'This employee has not enabled encrypted tasks yet. Ask them to sign in once in the jobs portal, then try again.',
        );
    }

    return encryptTaskPayload(
        { title, description },
        {
            companyPublicKeyBase64: companyKeyPair.publicKeyBase64,
            employeePublicKeyBase64,
        },
    );
};

export const prepareEncryptedSubmissionPayload = async ({
    signMessageAsync,
    employeeWalletAddress,
    companyWalletAddress,
    message,
    links = [],
    imageFiles = [],
    registerEncryptionPublicKey,
    fetchEncryptionPublicKey,
    uploadEncryptedFile,
}) => {
    const employeeKeyPair = await deriveEncryptionKeyPair(
        signMessageAsync,
        employeeWalletAddress,
    );
    await registerEncryptionPublicKey(employeeKeyPair.publicKeyBase64);

    let companyPublicKeyBase64;
    try {
        companyPublicKeyBase64 = await fetchEncryptionPublicKey(companyWalletAddress);
    } catch (_error) {
        throw new Error(
            'Company has not enabled encryption yet. Ask the company owner to sign in once in the jobs portal.',
        );
    }

    const submissionKey = await generateAesRawKey();
    const encryptedFiles = [];

    for (const file of imageFiles) {
        const fileBytes = new Uint8Array(await file.arrayBuffer());
        const sniffed = sniffSafeRasterMime(fileBytes);
        if (!sniffed) {
            throw new Error(
                `"${file.name || 'image'}" is not a safe PNG/JPEG/GIF/WEBP image`,
            );
        }
        const { iv, ciphertext } = await encryptBytesWithKey(fileBytes, submissionKey);
        const encBlob = new Blob([ciphertext], { type: 'application/octet-stream' });
        const safeName = String(file.name || 'image').replace(/\.[^.]+$/, '');
        const encFile = new File([encBlob], `${safeName}.enc`, {
            type: 'application/octet-stream',
        });
        const uploaded = await uploadEncryptedFile(encFile);
        if (!uploaded?.url) {
            throw new Error(`Encrypted upload failed for "${file.name}"`);
        }
        encryptedFiles.push({
            url: uploaded.url,
            name: file.name || 'image',
            mimeType: sniffed,
            iv,
        });
    }

    const { iv, ciphertext } = await aesGcmEncrypt(
        {
            message: String(message || '').trim(),
            links: (links || [])
                .map((item) => String(item || '').trim())
                .filter(Boolean),
            files: encryptedFiles,
        },
        submissionKey,
    );

    return {
        encryptionVersion: ENCRYPTION_VERSION,
        encryptedPayload: { iv, ciphertext },
        keyWrapCompany: wrapTaskKeyForRecipient(submissionKey, companyPublicKeyBase64),
        keyWrapEmployee: wrapTaskKeyForRecipient(
            submissionKey,
            employeeKeyPair.publicKeyBase64,
        ),
        message: ENCRYPTED_SUBMISSION_MESSAGE_PLACEHOLDER,
        attachments: [],
    };
};
